import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'

/**
 * The label a wait box shows on the Gantt, composed here and not in the backend.
 *
 * Sun sends the PIECES of the label -- the fabrication order code and the code and name of the job
 * the wait precedes (03-dominio.md §5.4 of story #146) -- and never the finished string: composing
 * it server-side would force the backend to pick a separator and a language, and neither is its
 * business.
 *
 * The rule, verbatim from the frozen contract:
 *
 *     fabricationOrderCode + separator + (jobCode when it has a value, otherwise jobName)
 *
 * and when neither the code nor the name of the job has a value, ONLY the fabrication order code:
 * no dangling separator, no filler literal, no "null" (RN39.2/D32). Old data makes this a real
 * case, not a defensive one: story #28 left the node name nullable.
 *
 * Nothing here goes through i18n: every piece is a datum, so there is no key to add (§5.6). The one exception is
 * the technology of a wait (story 204): a seeded technology reads by its code, like everywhere else.
 */

/**
 * The single separator of this view. One place, one value: a second separator somewhere else would
 * make two boxes of the same row read differently (RN39.1). It is the one the side panel already
 * uses to join a job with its asset, so the planner reads the same punctuation everywhere.
 */
export const WAIT_LABEL_SEPARATOR = ' · '

/**
 * Whether a field carried a value. `null`, `undefined` and the empty string are all "no value":
 * the contract types `jobCode`/`jobName` as nullable, and old rows arrive with empty strings.
 *
 * @param {*} value Field as it arrives from the API
 * @returns {boolean} True when there is something to show
 */
const hasValue = (value) => typeof value === 'string' && value.trim() !== ''

/**
 * The job of a wait as the planner names it: its code, and its name only when there is no code.
 *
 * @param {Object} wait One element of the `waits` list of a wait event
 * @returns {string} The job label, or '' when the job has neither code nor name
 */
export const waitJobLabel = (wait = {}) => {
  if (hasValue(wait?.jobCode)) {
    return wait.jobCode
  }
  return hasValue(wait?.jobName) ? wait.jobName : ''
}

/**
 * The fabrication order of a wait, the one piece of the label that is always there.
 *
 * @param {Object} wait One element of the `waits` list of a wait event
 * @returns {string} The fabrication order code, or '' when it is missing
 */
export const waitOrderLabel = (wait = {}) => (hasValue(wait?.fabricationOrderCode) ? wait.fabricationOrderCode : '')

// i18next's own fallback: the key when there is no translation and no defaultValue.
const untranslated = (key, options = {}) => options.defaultValue ?? key

/**
 * The technology of a wait as the planner names it: by its code when it is a seeded technology (story 204, RN2.1),
 * otherwise its name, and its code when there is no name.
 * An internal wait has no technology at all (`null` in the contract), and that reads as an empty
 * cell -- never as the word "null".
 *
 * @param {Object} wait One element of the `waits` list of a wait event
 * @param {function(string, Object=): string} [t_] The translator
 * @returns {string} The technology label, or '' when the wait has no technology
 */
export const waitTechnologyLabel = (wait = {}, t_ = untranslated) => {
  const label = systemValueLabel(t_, 'technology', wait?.technology || {})
  if (hasValue(label)) {
    return label
  }
  return hasValue(wait?.technology?.code) ? wait.technology.code : ''
}

/**
 * The full label of a wait box.
 *
 * @param {Object} wait One element of the `waits` list of a wait event
 * @returns {string} The label, or '' when there is not even a fabrication order code
 */
export const waitBoxLabel = (wait = {}) => {
  return [waitOrderLabel(wait), waitJobLabel(wait)]
    .filter((piece) => piece !== '')
    .join(WAIT_LABEL_SEPARATOR)
}
